<template>
  <div
    v-if="totalCount > 1"
    :aria-valuemax="totalCount"
    :aria-valuemin="1"
    :aria-valuenow="currentStep"
    class="oai-Stepper"
    role="progressbar"
  >
    <div
      v-for="stepNumber in totalCount"
      :key="stepNumber"
      :class="{
        'oai-Stepper_Item-passed': stepNumber > 1 && stepNumber <= currentStep
      }"
      class="oai-Stepper_Item"
    >
      <div
        :class="{
          'oai-Stepper_Bubble-passed': stepNumber < currentStep,
          'oai-Stepper_Bubble-active': stepNumber === currentStep,
          'oai-Stepper_Bubble-upcoming': stepNumber > currentStep
        }"
        :aria-current="stepNumber === currentStep ? 'step' : undefined"
        :aria-label="`${stepNumber} / ${totalCount}`"
        class="oai-Stepper_Bubble"
      >
        <AppIcon
          v-if="stepNumber < currentStep"
          class="oai-Stepper_CheckIcon"
          height="16"
          icon-name="check"
          width="16"
        />
        <span v-else class="oai-Stepper_Label">{{ stepNumber }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import AppIcon from '@/components/ui/AppIcon/AppIcon'

defineOptions({
  name: 'OnboardingAiStepper'
})

defineProps({
  totalCount: {
    type: Number,
    required: true,
    validator: v => Number.isInteger(v) && Number.isFinite(v) && v >= 0
  },

  currentStep: {
    type: Number,
    required: true,
    validator: (v, props) => {
      const isValid = v >= 0 && Number.isInteger(v) && Number.isFinite(v) && v <= props.totalCount

      if (!isValid) {
        throw new Error(
          `Invalid prop: currentStep must be a number between 0 and ${props.totalCount}`
        )
      }

      return isValid
    }
  }
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/onboarding-ai-step';

.oai-Stepper {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: 24px;
  margin-inline: auto;
}

.oai-Stepper_Item {
  display: flex;
  align-items: center;
  gap: 4px;

  &:first-child {
    flex: 0 0 auto;
  }

  &:not(:first-child) {
    flex: 1 1 0;

    &::before {
      content: '';
      flex: 1 1 auto;
      width: 25px;
      height: 1px;
      background-color: $grey-2-next;
    }
  }

  &-passed:not(:first-child)::before {
    background-color: $primary-color-next;
  }

  &:last-child {
    flex: 0 1 auto;
  }
}

.oai-Stepper_Bubble {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 24px;
  height: 24px;
  border: 1px solid $grey-2-next;
  border-radius: 50%;
  background-color: $white;

  &-passed,
  &-active {
    border-color: $primary-color-next;
    background-color: $primary-color-next;
  }
}

.oai-Stepper_Label {
  font-size: $fs-12;
  font-weight: fw('semi-bold');
  line-height: 18px;
  color: $oai-text-placeholder;

  .oai-Stepper_Bubble-active & {
    color: $white;
  }
}

.oai-Stepper_CheckIcon {
  color: $white;
}
</style>
